(() => {
  if (window.__mpsHomeV2AnimationsLoaded) return;
  window.__mpsHomeV2AnimationsLoaded = true;

  const motion = window.Motion;
  const heroSection = document.querySelector('[id^="MpsHomeHero-"]');

  if (!motion) return;

  document.documentElement.classList.add('mps-motion-ready');

  const prefersReducedMotion = Boolean(window.theme?.config?.motionReduced) || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const numberFormatter = new Intl.NumberFormat(document.documentElement.lang === 'fr' ? 'fr-FR' : undefined);

  const observeOnce = (target, callback, options = { amount: 0.2 }) => {
    if (!target) return;

    let hasRun = false;
    motion.inView(
      target,
      () => {
        if (hasRun) return;
        hasRun = true;
        callback(target);
      },
      options
    );
  };

  const revealNow = (elements) => {
    elements.forEach((element) => {
      element.style.opacity = '1';
      element.style.transform = 'none';
      element.style.clipPath = 'none';
    });
  };

  const wrapWords = (container) => {
    if (!container || container.dataset.mpsWordsReady === 'true') return;

    const textNodes = [];
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        if (!node.parentElement) return NodeFilter.FILTER_REJECT;
        if (node.parentElement.closest('script, style')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });

    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      const fragment = document.createDocumentFragment();
      const chunks = node.nodeValue.split(/(\s+)/);

      chunks.forEach((chunk) => {
        if (!chunk) return;
        if (/^\s+$/.test(chunk)) {
          fragment.appendChild(document.createTextNode(chunk));
          return;
        }

        const word = document.createElement('span');
        word.className = 'mps-hero-word';
        word.textContent = chunk;
        fragment.appendChild(word);
      });

      node.parentNode.replaceChild(fragment, node);
    });

    container.dataset.mpsWordsReady = 'true';
  };

  const initHero = () => {
    if (!heroSection) return;
    const eyebrow = heroSection.querySelector('[data-mps-hero-eyebrow]');
    const title = heroSection.querySelector('[data-mps-hero-title]');
    if (!title) return;

    wrapWords(title);
    const words = Array.from(title.querySelectorAll('.mps-hero-word'));

    if (prefersReducedMotion) {
      revealNow([title, ...(eyebrow ? [eyebrow] : []), ...words]);
      return;
    }

    if (eyebrow) {
      eyebrow.style.opacity = '0';
      eyebrow.style.transform = 'translateY(16px)';
    }

    words.forEach((word) => {
      word.style.opacity = '0';
      word.style.transform = 'translateY(20px)';
    });

    requestAnimationFrame(() => {
      if (eyebrow) {
        motion.animate(
          eyebrow,
          { opacity: [0, 1], transform: ['translateY(16px)', 'translateY(0px)'] },
          { duration: 0.5, easing: 'ease-out' }
        );
      }

      if (words.length > 0) {
        motion.animate(
          words,
          { opacity: [0, 1], transform: ['translateY(20px)', 'translateY(0px)'] },
          { duration: 0.45, delay: motion.stagger(0.08, { start: 0.15 }), easing: 'ease-out' }
        );
      }
    });
  };

  const initHeroDrop = () => {
    if (!heroSection) return;
    const drop = heroSection.querySelector('[data-mps-hero-drop]');
    if (!drop || prefersReducedMotion || isMobile) return;
    const dropStyles = getComputedStyle(drop);
    const offsetXRaw = dropStyles.getPropertyValue('--mps-drop-offset-x').trim();
    const offsetYRaw = dropStyles.getPropertyValue('--mps-drop-offset-y').trim();
    const offsetX = Number.parseFloat(offsetXRaw);
    const offsetY = Number.parseFloat(offsetYRaw);
    const currentOpacity = Number.parseFloat(dropStyles.opacity);
    const safeOffsetX = Number.isFinite(offsetX) ? offsetX : 0;
    const safeOffsetY = Number.isFinite(offsetY) ? offsetY : 0;
    const safeOpacity = Number.isFinite(currentOpacity) ? Math.max(0, Math.min(1, currentOpacity)) : 1;
    const travelDistance = Math.max(window.innerHeight * 0.95, 420);
    const endOffsetY = safeOffsetY + travelDistance;
    const startTransform = `translate(-50%, -50%) translate(${safeOffsetX}px, ${safeOffsetY}px)`;
    const endTransform = `translate(-50%, -50%) translate(${safeOffsetX}px, ${Math.round(endOffsetY)}px)`;

    motion.scroll(
      motion.animate(
        drop,
        {
          transform: [startTransform, endTransform],
          opacity: [safeOpacity, 0]
        },
        { easing: 'linear' }
      ),
      {
        target: heroSection,
        offset: ['start start', 'end start']
      }
    );
  };

  const initGenericReveals = () => {
    const revealTargets = Array.from(document.querySelectorAll('[data-mps-reveal]')).filter((item) => {
      if (item.closest('[id^="MpsHomeHero-"]')) return false;
      if (item.matches('[data-mps-video-player]')) return false;
      return true;
    });
    if (revealTargets.length === 0) return;

    if (prefersReducedMotion) {
      revealNow(revealTargets);
      return;
    }

    revealTargets.forEach((target) => {
      observeOnce(target, (element) => {
        motion.animate(
          element,
          { opacity: [0, 1], transform: ['translateY(24px)', 'translateY(0px)'] },
          { duration: 0.55, easing: 'ease-out' }
        );
      });
    });
  };

  const initStaggers = () => {
    const groups = Array.from(document.querySelectorAll('[data-mps-stagger]'));
    if (groups.length === 0) return;

    groups.forEach((group) => {
      const items = Array.from(group.querySelectorAll('[data-mps-stagger-item]'));
      if (items.length === 0) return;
      const fadeOnly = group.getAttribute('data-mps-stagger') === 'fade-only';

      if (prefersReducedMotion) {
        revealNow(items);
        return;
      }

      observeOnce(group, () => {
        if (fadeOnly) {
          motion.animate(
            items,
            { opacity: [0, 1] },
            { duration: 0.5, delay: motion.stagger(0.1), easing: 'ease-out' }
          );
        } else {
          motion.animate(
            items,
            { opacity: [0, 1], transform: ['translateY(30px)', 'translateY(0px)'] },
            { duration: 0.55, delay: motion.stagger(0.12), easing: 'ease-out' }
          );
        }
      }, { amount: fadeOnly ? 0.01 : 0.2 });
    });
  };

  const initVideoSection = () => {
    const videoSections = Array.from(document.querySelectorAll('[id^="MpsHomeVideo-"]'));
    if (videoSections.length === 0) return;

    videoSections.forEach((section) => {
      const player = section.querySelector('[data-mps-video-player]');
      const video = player?.querySelector('video');
      const unmuteButton = player?.querySelector('[data-mps-video-unmute]');
      const progressTrack = section.querySelector('[data-mps-video-progress]');
      const progressBar = section.querySelector('.mps-home-video__progress-bar');

      if (!player || !video) return;

      const syncProgress = () => {
        if (!progressBar || !Number.isFinite(video.duration) || video.duration <= 0) return;
        const progress = Math.max(0, Math.min(100, (video.currentTime / video.duration) * 100));
        progressBar.style.width = `${progress}%`;
      };

      const seekVideo = (ratio) => {
        if (!Number.isFinite(video.duration) || video.duration <= 0) return;
        const safeRatio = Math.max(0, Math.min(1, ratio));
        video.currentTime = safeRatio * video.duration;
        syncProgress();
      };

      const seekFromClientX = (clientX) => {
        if (!progressTrack || !Number.isFinite(clientX)) return;
        const rect = progressTrack.getBoundingClientRect();
        if (rect.width <= 0) return;
        seekVideo((clientX - rect.left) / rect.width);
      };

      const setUnmuteState = () => {
        if (!unmuteButton) return;
        const isMuted = video.muted;
        unmuteButton.setAttribute('aria-pressed', String(!isMuted));
        unmuteButton.setAttribute('aria-label', isMuted ? (unmuteButton.dataset.labelUnmute || 'Activer le son') : (unmuteButton.dataset.labelMute || 'Couper le son'));
        unmuteButton.setAttribute('data-muted', String(isMuted));
      };

      const toggleMute = () => {
        video.muted = !video.muted;
        video.play().catch(() => {});
        setUnmuteState();
      };

      video.muted = true;
      video.setAttribute('muted', 'muted');
      video.play().catch(() => {});
      setUnmuteState();
      syncProgress();

      if (unmuteButton) {
        unmuteButton.addEventListener('click', toggleMute);
      }

      if (progressTrack) {
        const onPointerDown = (event) => {
          seekFromClientX(event.clientX);
        };

        const onTouchStart = (event) => {
          const touch = event.touches?.[0];
          if (!touch) return;
          seekFromClientX(touch.clientX);
        };

        const onKeyDown = (event) => {
          if (!Number.isFinite(video.duration) || video.duration <= 0) return;
          const stepSeconds = 5;

          if (event.key === 'Home') {
            event.preventDefault();
            seekVideo(0);
            return;
          }

          if (event.key === 'End') {
            event.preventDefault();
            seekVideo(1);
            return;
          }

          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            video.currentTime = Math.max(0, video.currentTime - stepSeconds);
            syncProgress();
            return;
          }

          if (event.key === 'ArrowRight') {
            event.preventDefault();
            video.currentTime = Math.min(video.duration, video.currentTime + stepSeconds);
            syncProgress();
          }
        };

        if ('PointerEvent' in window) {
          progressTrack.addEventListener('pointerdown', onPointerDown);
        } else {
          progressTrack.addEventListener('mousedown', onPointerDown);
          progressTrack.addEventListener('touchstart', onTouchStart, { passive: true });
        }

        progressTrack.addEventListener('keydown', onKeyDown);
      }

      video.addEventListener('click', toggleMute);
      video.addEventListener('timeupdate', syncProgress);
      video.addEventListener('loadedmetadata', syncProgress);

      if (prefersReducedMotion) {
        revealNow([player]);
        return;
      }

      observeOnce(player, () => {
        motion.animate(
          player,
          { opacity: [0, 1], transform: ['scale(0.95)', 'scale(1)'] },
          { duration: 0.6, easing: 'ease-out' }
        );
      });
    });
  };

  const initSocialCounters = () => {
    const counters = Array.from(document.querySelectorAll('[data-mps-counter]'));
    if (counters.length === 0) return;

    counters.forEach((counter) => {
      const rawTarget = (counter.getAttribute('data-target') || '0').replace(/[^\d-]/g, '');
      const targetValue = parseInt(rawTarget || '0', 10);
      if (!Number.isFinite(targetValue) || targetValue <= 0) return;

      if (prefersReducedMotion) {
        counter.textContent = numberFormatter.format(targetValue);
        return;
      }

      observeOnce(counter, () => {
        const duration = 2000;
        const start = performance.now();

        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          const value = Math.round(targetValue * eased);
          counter.textContent = numberFormatter.format(value);

          if (progress < 1) requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
      });
    });
  };

  const initScience = () => {
    const scienceSections = Array.from(document.querySelectorAll('[id^="MpsHomeScience-"]'));
    if (scienceSections.length === 0) return;

    scienceSections.forEach((scienceSection) => {
      const pillarGroup = scienceSection.querySelector('[data-mps-stagger="science-pillars"]');
      const pillars = Array.from(scienceSection.querySelectorAll('[data-mps-stagger-item="science-pillar"]'));
      const background = scienceSection.querySelector('[data-mps-parallax="science-bg"]');

      if (prefersReducedMotion) {
        revealNow(pillars);
      } else if (pillarGroup && pillars.length > 0) {
        observeOnce(pillarGroup, () => {
          pillars.forEach((pillar, index) => {
            const delay = index * 0.2;
            const number = pillar.querySelector('.mps-home-science__pillar-num');
            const title = pillar.querySelector('.mps-home-science__pillar-title');
            const text = pillar.querySelector('.mps-home-science__pillar-text');

            motion.animate(
              pillar,
              { opacity: [0, 1], transform: ['translateX(30px)', 'translateX(0px)'] },
              { duration: 0.55, easing: 'ease-out', delay }
            );

            if (number) {
              motion.animate(
                number,
                { opacity: [0, 1], transform: ['scale(0)', 'scale(1)'] },
                { duration: 0.45, easing: motion.spring({ stiffness: 320, damping: 24 }), delay: delay + 0.05 }
              );
            }

            if (title) {
              motion.animate(
                title,
                { opacity: [0, 1], transform: ['translateX(16px)', 'translateX(0px)'] },
                { duration: 0.35, easing: 'ease-out', delay: delay + 0.12 }
              );
            }

            if (text) {
              motion.animate(
                text,
                { opacity: [0, 1], transform: ['translateX(16px)', 'translateX(0px)'] },
                { duration: 0.4, easing: 'ease-out', delay: delay + 0.2 }
              );
            }
          });
        });
      }

      if (!prefersReducedMotion && !isMobile && background) {
        motion.scroll(
          motion.animate(
            background,
            { transform: ['translateY(-4%)', 'translateY(6%)'] },
            { easing: 'linear' }
          ),
          { target: scienceSection, offset: ['start end', 'end start'] }
        );
      }
    });
  };

  const initFaqAutoclose = () => {
    const faqContainers = Array.from(document.querySelectorAll('.faqs .faq'));
    if (faqContainers.length === 0) return;

    faqContainers.forEach((faqContainer) => {
      if (faqContainer.querySelector('accordions-details')) return;

      const detailItems = Array.from(faqContainer.querySelectorAll('details[is="accordion-details"]'));
      if (detailItems.length < 2) return;

      const wrapper = document.createElement('accordions-details');
      wrapper.className = 'mps-faq-autoclose';

      const firstAccordion = detailItems[0].closest('.accordion');
      if (!firstAccordion) return;

      // Find a safe insertion node that is guaranteed to be a direct child of faqContainer.
      let insertionNode = firstAccordion;
      while (insertionNode && insertionNode.parentElement !== faqContainer) {
        insertionNode = insertionNode.parentElement;
      }

      if (insertionNode && insertionNode.parentElement === faqContainer) {
        faqContainer.insertBefore(wrapper, insertionNode);
      } else {
        // Fallback for unexpected nested markup: keep behavior stable without throwing.
        faqContainer.prepend(wrapper);
      }

      let movedCount = 0;
      const movedAccordions = [];
      detailItems.forEach((item) => {
        const accordion = item.closest('.accordion');
        if (!accordion || accordion.closest('accordions-details')) return;

        // Only move accordions when they are direct children of faqContainer.
        if (accordion.parentElement === faqContainer) {
          movedAccordions.push({
            accordion,
            nextSibling: accordion.nextSibling
          });
          wrapper.appendChild(accordion);
          movedCount += 1;
        }
      });

      if (movedCount < 2) {
        // Restore any moved accordions so no content is lost.
        movedAccordions.forEach(({ accordion, nextSibling }) => {
          if (!accordion) return;

          if (nextSibling && nextSibling.parentElement === faqContainer) {
            faqContainer.insertBefore(accordion, nextSibling);
          } else {
            faqContainer.appendChild(accordion);
          }
        });

        wrapper.remove();

        // Fallback autoclose when structure is nested and cannot be safely regrouped.
        detailItems.forEach((item) => {
          if (item.dataset.mpsAutocloseBound === 'true') return;
          item.dataset.mpsAutocloseBound = 'true';

          item.addEventListener('toggle', () => {
            if (!item.hasAttribute('open')) return;
            detailItems.forEach((other) => {
              if (other !== item && typeof other.close === 'function') {
                other.close();
              }
            });
          });
        });
      }
    });
  };

  const initCertificationReveal = () => {
    const normalize = (value) => value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const candidates = Array.from(document.querySelectorAll('.section .banner__title'));
    const certificationTitle = candidates.find((title) => normalize(title.textContent || '').includes('exigence et securite'));

    if (!certificationTitle) return;

    const section = certificationTitle.closest('.section');
    if (!section) return;

    const titles = Array.from(section.querySelectorAll('.banner__title'));
    if (titles.length === 0) return;

    if (prefersReducedMotion) {
      revealNow(titles);
      return;
    }

    titles.forEach((title) => {
      title.style.opacity = '0';
      title.style.clipPath = 'inset(0 100% 0 0)';
    });

    observeOnce(section, () => {
      titles.forEach((title, index) => {
        motion.animate(
          title,
          { opacity: [0, 1], clipPath: ['inset(0 100% 0 0)', 'inset(0 0% 0 0)'] },
          { duration: 0.6, easing: 'ease-out', delay: index * 0.12 }
        );
      });
    }, { amount: 0.3 });
  };

  const initIconsStagger = () => {
    const iconSections = Array.from(document.querySelectorAll('.features-icons-section'));
    if (iconSections.length === 0) return;

    iconSections.forEach((iconSection) => {
      const items = Array.from(iconSection.querySelectorAll('.feature-item'));
      if (items.length === 0) return;

      if (prefersReducedMotion) {
        revealNow(items);
        return;
      }

      observeOnce(iconSection, () => {
        motion.animate(
          items,
          { opacity: [0, 1], transform: ['translateY(16px) scale(0.92)', 'translateY(0px) scale(1)'] },
          { duration: 0.45, easing: 'ease-out', delay: motion.stagger(0.1) }
        );
      });
    });
  };

  initHero();
  initHeroDrop();
  initGenericReveals();
  initStaggers();
  initVideoSection();
  initSocialCounters();
  initScience();
  initFaqAutoclose();
  initCertificationReveal();
  initIconsStagger();
})();
